<template>
  <!-- Safari installs the app from a menu, never from a dialog the page can
       open: all we can do is say where that menu is. Three steps at most, each
       with the glyph the person is looking for, because they will be reading
       this with their thumb already on the screen. -->
  <UiModal :title="t(`pwa.guide.title.${platform}`)" size="sm" @close="emit('close')">
    <ol class="guide">
      <li v-for="(step, index) in steps" :key="index" class="guide__step">
        <span class="guide__number">{{ index + 1 }}</span>
        <span class="guide__text">
          <span v-if="step.icon" class="guide__glyph"><AppIcon :name="step.icon" :size="18" /></span>
          {{ step.text }}
        </span>
      </li>
    </ol>
    <p class="guide__after">{{ t('pwa.guide.after') }}</p>
  </UiModal>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiModal } from '@general/ui-kit'

import AppIcon from './AppIcon.vue'

const props = defineProps({
  // What installGuide.js decided: 'iphone' | 'ipad' | 'mac'.
  platform: { type: String, required: true },
})
const emit = defineEmits(['close'])

const { t } = useI18n()

const steps = computed(() => (props.platform === 'mac'
  ? [
      { text: t('pwa.guide.mac.menu') },
      { text: t('pwa.guide.mac.dock') },
      { text: t('pwa.guide.mac.confirm') },
    ]
  : [
      // Where the Share button is differs: under the address on an iPhone
      // (inside «···» on the newest Safari), beside it on an iPad.
      { text: t(`pwa.guide.ios.share.${props.platform}`), icon: 'share' },
      { text: t('pwa.guide.ios.add'), icon: 'add-app' },
      { text: t('pwa.guide.ios.confirm') },
    ]))
</script>

<style scoped>
.guide {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: 0;
  padding: 0;
  list-style: none;
}
.guide__step { display: flex; align-items: flex-start; gap: var(--space-3); }
.guide__number {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: var(--text-sm);
  font-weight: 600;
}
.guide__text { padding-top: 3px; font-size: var(--text-sm); line-height: 1.5; }
.guide__glyph {
  display: inline-flex;
  vertical-align: -4px;
  margin-right: var(--space-1);
  color: var(--color-primary);
}
.guide__after { margin: var(--space-4) 0 0; font-size: var(--text-xs); color: var(--color-text-mute); }
</style>
